import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { NewOrderItem } from 'types';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';
import {
  updateOrderItemTrackingNumber,
  updateOrderItemCarrier,
  storeOriginalOrderItems,
} from 'store/slices/shipping/orderItemChanges';
import { styles } from './style';
import { ShipmentCarrier } from 'api/rest/shipmentCarriers/types';
import { Select, MenuItem } from '@mui/material';
import { detectCarrierFromTrackingNumber } from './utils';

interface EditOrderItemsModalProps {
  open: boolean;
  onClose: () => void;
  selectedOrderItems: NewOrderItem[];
  carriers: ShipmentCarrier[];
  loadingCarriers: boolean;
  onSuccess?: () => void;
}

const EditOrderItemsModal: React.FC<EditOrderItemsModalProps> = ({
  open,
  onClose,
  selectedOrderItems,
  carriers = [],
  loadingCarriers,
  onSuccess,
}) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const dispatch = useDispatch();

  // Reset input fields when modal is closed
  useEffect(() => {
    if (!open) {
      setTrackingNumber('');
      setCarrierId('');
    }
  }, [open]);

  const handleTrackingNumberBlur = () => {
    const detectedCarrierId = detectCarrierFromTrackingNumber(
      trackingNumber,
      carriers
    );
    if (detectedCarrierId) {
      setCarrierId(detectedCarrierId);
    }
  };

  const handleApplyAllChanges = () => {
    const hasTrackingNumber = trackingNumber.trim() !== '';
    const hasCarrier = carrierId.trim() !== '';

    if (!hasTrackingNumber && !hasCarrier) return;

    // Store original order items in Redux
    dispatch(storeOriginalOrderItems(selectedOrderItems));

    // Update each order item in Redux store
    selectedOrderItems.forEach((orderItem) => {
      if (hasTrackingNumber) {
        dispatch(
          updateOrderItemTrackingNumber({
            id: orderItem.id,
            orderId: orderItem.order?.id || '',
            trackingNumber,
            originalOrderItem: orderItem,
          })
        );
      }

      if (hasCarrier) {
        dispatch(
          updateOrderItemCarrier({
            id: orderItem.id,
            orderId: orderItem.order?.id || '',
            carrierId,
            originalOrderItem: orderItem,
          })
        );
      }
    });

    if (onSuccess) {
      onSuccess();
    }

    onClose();
  };

  const hasChanges = trackingNumber.trim() !== '' || carrierId !== '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: styles.dialog.paper,
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6" sx={styles.titleTypography}>
          Bulk edit {selectedOrderItems.length} items
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={styles.sectionTitle}>
            Tracking number
          </Typography>
          <TextField
            placeholder="Enter tracking number"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            onBlur={handleTrackingNumberBlur}
            fullWidth
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={styles.sectionTitle}>
            Tracking carrier
          </Typography>
          <Select
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography sx={{ color: 'text.disabled' }}>
                    Select tracking carrier
                  </Typography>
                );
              }
              return carriers
                .find((carrier) => carrier.id === selected)
                ?.name?.toUpperCase();
            }}
            disabled={loadingCarriers}
            value={carrierId}
            onChange={(e) => setCarrierId(e.target.value)}
            fullWidth
          >
            {loadingCarriers ? (
              <MenuItem value="">Loading carriers...</MenuItem>
            ) : (
              carriers.map((carrier) => (
                <MenuItem key={carrier.id} value={carrier.id}>
                  {carrier.name?.toUpperCase()}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="text"
          color="secondary"
          onClick={onClose}
          size="small"
          sx={styles.button}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApplyAllChanges}
          size="small"
          disabled={!hasChanges}
          sx={styles.button}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderItemsModal;
